import { useState, useEffect, useCallback } from "react";
import "./App.css";
import axios from "axios";
import { Link } from "react-router-dom";
import { Activity, RefreshCw, Network, ChevronRight, Wifi, WifiOff, ArrowLeft, Tv, Phone } from "lucide-react";
import { STATUS_CONFIG } from "./statusConfig";
import { StatusBadge } from "./components/StatusBadge";
import { ThemeToggle } from "./ThemeContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const REFRESH_INTERVAL = 10;

// Flatten tree into a list of rows with depth metadata.
// For child sites (depth > 0) with incoming link data, link-row pseudo-entries are
// injected BEFORE the site row so they appear under the parent visually.
function flattenTree(nodes, expandedIds, result, depth, ancestorIsLast) {
  if (!nodes) return;
  for (var i = 0; i < nodes.length; i++) {
    var node = nodes[i];
    // Skip old-format failover pseudo-nodes (no longer used by backend)
    if (node.type === 'failover') continue;
    var isLast = (i === nodes.length - 1);
    var curAncestors = ancestorIsLast ? ancestorIsLast.slice() : [];

    // Inject link rows (MAIN + FAILOVER + OUTAGE) before each child site
    if (depth > 0) {
      var allLinks = [];
      if (node.ptp_device) {
        var failovers = node.ptp_failover_links || [];
        var hasDeadLink = failovers.some(function(f) { return f.link_dead === true; });
        var mainLabel = 'MAIN';
        if (hasDeadLink) {
          // Site is running on its backup — label based on how loaded it is
          var maxUtil = Math.max(node.ptp_dl || 0, node.ptp_ul || 0);
          mainLabel = maxUtil >= 90 ? 'DEGRADED' : 'ACTIVE FAILOVER';
        }
        allLinks.push({ label: mainLabel, dl: node.ptp_dl, ul: node.ptp_ul,
                        device: node.ptp_device || '', ifaceDesc: node.ptp_iface_desc || '',
                        linkDead: false });
        for (var f = 0; f < failovers.length; f++) {
          var isDead = failovers[f].link_dead === true;
          allLinks.push({ label: isDead ? 'OUTAGE' : 'BACKUP LINK',
                          dl: failovers[f].dl, ul: failovers[f].ul,
                          device: failovers[f].device, ifaceDesc: failovers[f].iface_desc || '',
                          linkDead: isDead });
        }
      }  // end if (node.ptp_device)
      for (var l = 0; l < allLinks.length; l++) {
        result.push({
          node: { type: 'link-row', id: 'link-' + node.id + '-' + l,
                  label: allLinks[l].label, dl: allLinks[l].dl, ul: allLinks[l].ul,
                  device: allLinks[l].device, ifaceDesc: allLinks[l].ifaceDesc,
                  linkDead: allLinks[l].linkDead },
          depth: depth,
          isLast: false,             // site row always follows
          ancestorIsLast: curAncestors,
          linkIsFirst: l === 0,
          linkIsLast: l === allLinks.length - 1,
        });
      }
    }

    result.push({ node: node, depth: depth, isLast: isLast, ancestorIsLast: curAncestors });
    if (node.children && node.children.length > 0 && expandedIds[node.id] === true) {
      flattenTree(node.children, expandedIds, result, depth + 1, curAncestors.concat([isLast]));
    }
  }
}

function OverallBanner(props) {
  var status = props.status;
  var loading = props.loading;
  var cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  var Icon = cfg.Icon;
  return (
    <div
      data-testid="overall-status-banner"
      aria-live="polite"
      className={cfg.bannerClass + " p-6 sm:p-8 transition-colors duration-500"}
    >
      <div className="flex items-start gap-4">
        <Icon
          size={28}
          className={cfg.bannerIconClass + " mt-0.5 flex-shrink-0" + (loading && status === "unknown" ? " animate-spin" : "")}
          strokeWidth={2}
        />
        <div>
          <h2
            data-testid="overall-status-label"
            className={"text-xl sm:text-2xl font-semibold tracking-tight " + cfg.bannerTextClass}
          >
            {cfg.bannerLabel}
          </h2>
          <p className={"mt-1 text-sm opacity-75 " + cfg.bannerTextClass}>
            {cfg.bannerSubtitle}
          </p>
        </div>
      </div>
    </div>
  );
}

function latencyColor(ms) {
  if (ms < 30) return "text-emerald-600";
  if (ms < 50) return "text-amber-500";
  return "text-red-500";
}

var LINE = "var(--tree-line)";
var CELL_W = 20;

// Pure tree connector: vertical/horizontal lines only, no badge.
function TreeConnector(props) {
  var depth = props.depth;
  var isLast = props.isLast;
  var ancestorIsLast = props.ancestorIsLast;
  if (depth === 0) return null;
  var ancestorCells = [];
  for (var i = 0; i < ancestorIsLast.length; i++) {
    ancestorCells.push(
      <div key={i} style={{ width: CELL_W, flexShrink: 0, alignSelf: "stretch", position: "relative" }}>
        {!ancestorIsLast[i] && (
          <div style={{ position: "absolute", left: 9, top: 0, bottom: 0, width: 1, backgroundColor: LINE }} />
        )}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexShrink: 0, alignSelf: "stretch" }}>
      {ancestorCells}
      <div style={{ width: 28, flexShrink: 0, alignSelf: "stretch", position: "relative" }}>
        <div style={{ position: "absolute", left: 9, top: 0, height: "50%", width: 1, backgroundColor: LINE }} />
        {!isLast && (
          <div style={{ position: "absolute", left: 9, top: "50%", bottom: 0, width: 1, backgroundColor: LINE }} />
        )}
        <div style={{ position: "absolute", left: 9, top: "50%", width: 19, height: 1, backgroundColor: LINE }} />
      </div>
    </div>
  );
}

// Link row: shows MAIN (filled green dot + solid line) or FAILOVER (hollow dot + dashed line).
// Dot-connector rows appear before each child site, under the parent in the expanded view.
function LinkRow(props) {
  var node = props.node;
  var depth = props.depth;
  var ancestorIsLast = props.ancestorIsLast;
  var linkIsFirst = props.linkIsFirst;
  var linkIsLast = props.linkIsLast;
  var isMain           = node.label === 'MAIN';
  var isActiveFailover = node.label === 'ACTIVE FAILOVER';
  var isDegradedFail   = node.label === 'DEGRADED';
  var isOutage         = node.label === 'OUTAGE';
  var isBackup         = node.label === 'BACKUP LINK';
  var isActivePrimary  = isMain || isActiveFailover || isDegradedFail;
  var DOT_X = 9;

  var ancestorCells = [];
  for (var i = 0; i < ancestorIsLast.length; i++) {
    ancestorCells.push(
      <div key={i} style={{ width: CELL_W, flexShrink: 0, alignSelf: "stretch", position: "relative" }}>
        {!ancestorIsLast[i] && (
          <div style={{ position: "absolute", left: 9, top: 0, bottom: 0, width: 1, backgroundColor: LINE }} />
        )}
      </div>
    );
  }

  // Dot colour + glow
  var dotStyle = isMain
    ? { backgroundColor: "#22c55e", boxShadow: "0 0 0 2.5px #dcfce7" }
    : isActiveFailover
      ? { backgroundColor: "#3b82f6", boxShadow: "0 0 0 2.5px #bfdbfe" }
      : isDegradedFail
        ? { backgroundColor: "#f97316", boxShadow: "0 0 0 2.5px #fed7aa" }
        : isOutage
          ? { backgroundColor: "#ef4444", boxShadow: "0 0 0 2.5px #fee2e2" }
          : isBackup
            ? { border: "2px solid #60a5fa", backgroundColor: "#eff6ff" }
            : { border: "2px solid #a3a3a3", backgroundColor: "white" };

  // Badge colour
  var badgeStyle = isMain
    ? { color: "#16a34a", backgroundColor: "#f0fdf4", border: "1px solid #86efac" }
    : isActiveFailover
      ? { color: "#1d4ed8", backgroundColor: "#eff6ff", border: "1px solid #93c5fd" }
      : isDegradedFail
        ? { color: "#c2410c", backgroundColor: "#fff7ed", border: "1px solid #fdba74" }
        : isOutage
          ? { color: "#b91c1c", backgroundColor: "#fef2f2", border: "1px solid #fca5a5" }
          : isBackup
            ? { color: "#0369a1", backgroundColor: "#f0f9ff", border: "1px solid #bae6fd" }
            : { color: "#b45309", backgroundColor: "#fef3c7", border: "1px solid #fcd34d" };

  return (
    <div
      data-testid={"link-row-" + node.id}
      className="flex items-stretch"
      style={{ paddingRight: 4 }}
    >
      {/* Ancestor tree continuation lines */}
      <div style={{ display: "flex", flexShrink: 0, alignSelf: "stretch" }}>
        {ancestorCells}
      </div>

      {/* Dashed vertical connector between dots + circle dot + horizontal line */}
      <div style={{ width: 36, flexShrink: 0, alignSelf: "stretch", position: "relative", display: "flex", alignItems: "center" }}>
        {!linkIsFirst && (
          <div style={{ position: "absolute", left: DOT_X, top: 0, height: "50%", borderLeft: "1.5px dashed var(--tree-line)" }} />
        )}
        {!linkIsLast && (
          <div style={{ position: "absolute", left: DOT_X, top: "50%", bottom: 0, borderLeft: "1.5px dashed var(--tree-line)" }} />
        )}
        {/* Horizontal connector to badge */}
        <div style={{
          position: "absolute", left: DOT_X + 6, top: "50%", width: 20, height: 1,
          ...(isActivePrimary ? { backgroundColor: LINE } : { borderTop: "1px dashed var(--tree-line)" })
        }} />
        {/* Dot */}
        <div style={{
          position: "absolute", left: DOT_X - 5, top: "50%", transform: "translateY(-50%)",
          width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
          ...dotStyle
        }} />
      </div>

      {/* Badge + device + stats */}
      <div className="flex items-center flex-1 py-2 gap-3 min-w-0" style={{ paddingLeft: 2 }}>
        <span
          data-testid={isMain ? "main-badge" : isActiveFailover ? "active-failover-badge" : isDegradedFail ? "degraded-failover-badge" : isOutage ? "outage-badge" : isBackup ? "backup-link-badge" : "failover-badge"}
          style={{
            fontSize: 9, fontFamily: "monospace", fontWeight: 700, letterSpacing: "0.07em",
            padding: "2px 7px", borderRadius: 4, flexShrink: 0,
            ...badgeStyle
          }}
        >
          {node.label}
        </span>
        <span style={{
          fontSize: 12, fontFamily: "monospace",
          color: isMain ? "#4b5563" : isOutage ? "#9ca3af" : isActiveFailover ? "#374151" : isDegradedFail ? "#374151" : isBackup ? "#0369a1" : "#9ca3af",
          flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          textDecoration: isOutage ? "line-through" : "none",
        }}>
          {node.device}
        </span>
        {node.ifaceDesc && /GHz|MHz/i.test(node.ifaceDesc) && (
          <span style={{
            fontSize: 9, borderRadius: 3, padding: "1px 5px",
            fontFamily: "monospace", flexShrink: 0, whiteSpace: "nowrap",
            ...(node.ifaceDesc === '5 GHz'
              ? { color: "#c2410c", backgroundColor: "#fff7ed", border: "1px solid #fed7aa" }
              : { color: "#6b7280", backgroundColor: "#f3f4f6", border: "1px solid #e5e7eb" })
          }}>
            {node.ifaceDesc}
          </span>
        )}
        <div style={{ display: "flex", gap: 3, fontSize: 11, fontFamily: "monospace", flexShrink: 0 }}>
          {isOutage ? (
            <span style={{ color: "#ef4444", opacity: 0.7 }}>Link Down</span>
          ) : isBackup ? (
            <span style={{ color: "#7dd3fc", letterSpacing: "0.02em" }}>&darr;{node.dl}% / &uarr;{node.ul}%</span>
          ) : (
            <>
              <span style={{ color: "#16a34a" }}>&darr;{node.dl}%</span>
              <span style={{ color: "#d1d5db" }}>/</span>
              <span style={{ color: "#6366f1" }}>&uarr;{node.ul}%</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SiteRow(props) {
  var node = props.node;
  var depth = props.depth;
  var isLast = props.isLast;
  var ancestorIsLast = props.ancestorIsLast;
  var expanded = props.expanded;
  var onToggle = props.onToggle;
  var hasChildren = !!(node.children && node.children.length > 0);
  var cfgKey = (node.status === 'degraded' && node.ptp_has_dead_link) ? 'failover' : node.status;
  var cfg = STATUS_CONFIG[cfgKey] || STATUS_CONFIG.operational;
  var showGroupBadge = false;

  return (
    <div
      data-testid={"site-row-" + node.id}
      className="flex items-stretch hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors duration-150"
      style={{ paddingRight: "4px" }}
    >
      {/* Tree connector lines (no badge — stats live in link rows) */}
      <TreeConnector
        depth={depth}
        isLast={isLast}
        ancestorIsLast={ancestorIsLast}
      />

      {/* Site content */}
      <div
        className="flex items-center justify-between flex-1 py-3.5 gap-3 min-w-0"
        style={{ paddingLeft: depth === 0 ? 4 : 0 }}
      >
        <div className="flex items-start gap-1.5 min-w-0">
          <button
            onClick={function() { if (hasChildren) onToggle(node.id); }}
            className={
              "mt-0.5 flex-shrink-0 w-5 h-5 flex items-center justify-center transition-colors rounded " +
              (hasChildren
                ? "text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 cursor-pointer"
                : "cursor-default text-transparent")
            }
            data-testid={hasChildren ? "site-expand-" + node.id : undefined}
          >
            <ChevronRight
              size={13}
              strokeWidth={2.5}
              className={"transition-transform duration-150 " + (expanded ? "rotate-90" : "")}
            />
          </button>

          {node.status === "outage"
            ? <WifiOff size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
            : <Wifi size={14} className={cfg.badgeIconClass + " flex-shrink-0 mt-0.5"} />
          }

          <div className="min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span
                data-testid={"site-name-" + node.id}
                className="text-sm font-medium text-zinc-900 dark:text-zinc-100"
              >
                {node.name}
              </span>
              {node.latency_ms != null && (
                <span
                  data-testid={"site-latency-" + node.id}
                  className={"text-xs font-mono " + latencyColor(node.latency_ms)}
                >
                  {node.latency_ms} ms
                </span>
              )}
            </div>
            {node.device_outage_count > 0 && node.status !== "outage" && (
              <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">
                {node.device_outage_count} of {node.device_count} device{node.device_count !== 1 ? "s" : ""} offline
              </p>
            )}
            {node.status === "degraded" && node.ptp_utilization >= 90 && (
              <p className="text-xs text-amber-600 mt-0.5">
                PtP link: {node.ptp_utilization}% utilization
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {showGroupBadge && (
            <div className="hidden sm:flex flex-col items-end gap-0.5">
              <StatusBadge status={node.group_status} dataTestId={"site-group-badge-" + node.id} />
              <span className="text-xs text-zinc-400 dark:text-zinc-500 leading-none pr-0.5">group</span>
            </div>
          )}
          <StatusBadge status={cfgKey} dataTestId={"site-status-badge-" + node.id} />
        </div>
      </div>
    </div>
  );
}

function RefreshCountdown(props) {
  var countdown = props.countdown;
  var refreshing = props.refreshing;
  return (
    <div
      data-testid="refresh-countdown"
      className="flex items-center gap-1.5 text-xs font-mono text-zinc-400 dark:text-zinc-500 uppercase tracking-widest"
    >
      <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} strokeWidth={2} />
      {refreshing ? "Refreshing\u2026" : "Refreshes in " + countdown + "s"}
    </div>
  );
}

export default function App() {
  var stateArr = useState(null);
  var data = stateArr[0];
  var setData = stateArr[1];

  var loadingArr = useState(true);
  var loading = loadingArr[0];
  var setLoading = loadingArr[1];

  var refreshingArr = useState(false);
  var refreshing = refreshingArr[0];
  var setRefreshing = refreshingArr[1];

  var errorArr = useState(null);
  var error = errorArr[0];
  var setError = errorArr[1];

  var countdownArr = useState(REFRESH_INTERVAL);
  var countdown = countdownArr[0];
  var setCountdown = countdownArr[1];

  // expandedIds: object map id -> boolean (default collapsed; true = expanded)
  var expandedArr = useState({});
  var expandedIds = expandedArr[0];
  var setExpandedIds = expandedArr[1];

  var servicesArr = useState([]);
  var services = servicesArr[0];
  var setServices = servicesArr[1];

  var fetchStatus = useCallback(function(isRefresh) {
    if (isRefresh) setRefreshing(true);
    axios.get(API + "/service-status").then(function(res) {
      setServices(Array.isArray(res.data && res.data.services) ? res.data.services : []);
    }).catch(function() { /* non-critical, ignore */ });
    return axios.get(API + "/status").then(function(res) {
      setData(res.data);
      setError(null);
    }).catch(function() {
      setError("Unable to reach the status service.");
    }).finally(function() {
      setLoading(false);
      setRefreshing(false);
      setCountdown(REFRESH_INTERVAL);
    });
  // API and REFRESH_INTERVAL are module-level constants; state setters are stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function() { fetchStatus(); }, [fetchStatus]);

  useEffect(function() {
    var prevTitle = document.title;
    document.title = "Network Status — Briarwood Internet";
    var desc = document.querySelector('meta[name="description"]');
    var prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc) desc.setAttribute("content", "Live network status for Briarwood Internet's Chatham-Kent wireless network — real-time site health, outages and uptime.");
    return function() {
      document.title = prevTitle;
      if (desc && prevDesc !== null) desc.setAttribute("content", prevDesc);
    };
  }, []);

  useEffect(function() {
    var interval = setInterval(function() { fetchStatus(true); }, REFRESH_INTERVAL * 1000);
    return function() { clearInterval(interval); };
  }, [fetchStatus]);

  // setCountdown is a stable React state setter — empty deps array is intentional
  useEffect(function() {
    var tick = setInterval(function() {
      setCountdown(function(c) { return c > 1 ? c - 1 : REFRESH_INTERVAL; });
    }, 1000);
    return function() { clearInterval(tick); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleToggle(id) {
    setExpandedIds(function(prev) {
      var next = Object.assign({}, prev);
      next[id] = prev[id] === true ? false : true;
      return next;
    });
  }

  var overall = loading ? "unknown" : (data && data.overall_status) || "unknown";
  var tree = (data && data.tree) || [];
  var timestamp = data && data.timestamp;

  // Flatten tree for rendering
  var flatRows = [];
  flattenTree(tree, expandedIds, flatRows, 0, []);

  // Count real site nodes only (skip link-row pseudo-entries from flattenTree)
  function countNodes(nodes) {
    var n = 0;
    for (var i = 0; i < nodes.length; i++) {
      n += 1 + countNodes(nodes[i].children || []);
    }
    return n;
  }
  var displayedCount = countNodes(tree);

  function formatTimestamp(ts) {
    if (!ts) return "\u2014";
    return new Date(ts).toLocaleString("en-CA", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      timeZoneName: "short",
    });
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-[#05050A] font-body">
      {refreshing && (
        <div className="fixed top-0 left-0 right-0 h-0.5 bg-zinc-200 dark:bg-zinc-700 z-50">
          <div className="h-full bg-zinc-400 animate-pulse w-full" />
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

        <header className="py-8 sm:py-12 border-b border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/"
              data-testid="status-back-home"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors group"
            >
              <ArrowLeft size={15} strokeWidth={2.2} className="transition-transform group-hover:-translate-x-0.5" />
              Back to Briarwood Internet
            </Link>
            <ThemeToggle />
          </div>
          <div className="flex items-center gap-2.5">
            <Activity size={16} className="text-zinc-400" strokeWidth={2} />
            <span className="text-xs font-mono text-zinc-400 dark:text-zinc-500 uppercase tracking-widest">
              Network Status
            </span>
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-white font-heading">
            Briarwood Internet
          </h1>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            Real-time status of our network infrastructure.
          </p>
        </header>

        <section className="mt-8">
          {error ? (
            <div data-testid="error-banner" className="border-l-4 border-zinc-400 dark:border-zinc-600 bg-zinc-100 dark:bg-zinc-800 p-6">
              <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{error}</p>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Retrying automatically\u2026</p>
            </div>
          ) : (
            <OverallBanner status={overall} loading={loading} />
          )}
        </section>

        <section className="mt-10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Network size={13} className="text-zinc-400" strokeWidth={2} />
              <h2 className="text-xs font-mono text-zinc-500 dark:text-zinc-400 uppercase tracking-widest">
                Network Hierarchy
              </h2>
            </div>
            {!loading && (
              <span className="text-xs text-zinc-400 dark:text-zinc-500 font-mono">
                {displayedCount} site{displayedCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {loading ? (
            <div data-testid="sites-loading" className="border-t border-b border-zinc-200 dark:border-zinc-800">
              {[0,1,2,3,4].map(function(i) {
                return (
                  <div key={i} className="flex items-center justify-between py-3.5 px-1 border-b border-zinc-100 dark:border-zinc-800 last:border-0">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-zinc-100 dark:bg-zinc-800 rounded animate-pulse" />
                      <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded animate-pulse w-36" />
                    </div>
                    <div className="h-6 bg-zinc-200 dark:bg-zinc-700 rounded-sm animate-pulse w-24" />
                  </div>
                );
              })}
            </div>
          ) : flatRows.length === 0 ? (
            <div data-testid="sites-empty" className="border-t border-b border-zinc-200 dark:border-zinc-800 py-10 text-center text-sm text-zinc-400 dark:text-zinc-500">
              No sites available yet.
            </div>
          ) : (
            <div data-testid="sites-list" className="border-t border-b border-zinc-200 dark:border-zinc-800 divide-y divide-zinc-100 dark:divide-zinc-800">
              {flatRows.map(function(row) {
                if (row.node.type === 'link-row') {
                  return (
                    <LinkRow
                      key={row.node.id}
                      node={row.node}
                      depth={row.depth}
                      ancestorIsLast={row.ancestorIsLast}
                      linkIsFirst={row.linkIsFirst}
                      linkIsLast={row.linkIsLast}
                    />
                  );
                }
                var isExpanded = expandedIds[row.node.id] === true;
                return (
                  <SiteRow
                    key={row.node.id}
                    node={row.node}
                    depth={row.depth}
                    isLast={row.isLast}
                    ancestorIsLast={row.ancestorIsLast}
                    expanded={isExpanded}
                    onToggle={handleToggle}
                  />
                );
              })}
            </div>
          )}
        </section>

        {services.length > 0 && (
          <section className="mt-10" data-testid="other-services-section">
            <div className="flex items-center gap-2 mb-3">
              <Tv size={13} className="text-zinc-400" strokeWidth={2} />
              <h2 className="text-xs font-mono text-zinc-500 dark:text-zinc-400 uppercase tracking-widest">
                Other Services
              </h2>
            </div>
            <div className="border-t border-b border-zinc-200 dark:border-zinc-800 divide-y divide-zinc-100 dark:divide-zinc-800">
              {services.map(function(s) {
                return (
                  <div key={s.id} data-testid={"service-row-" + s.id} className="flex items-center justify-between py-3.5 px-1 gap-3">
                    <div className="flex items-start gap-2 min-w-0">
                      {s.name === "Phone"
                        ? <Phone size={15} className="text-zinc-400 mt-0.5 shrink-0" strokeWidth={2} />
                        : <Tv size={15} className="text-zinc-400 mt-0.5 shrink-0" strokeWidth={2} />}
                      <div className="min-w-0">
                        <div data-testid={"service-name-" + s.id} className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{s.name}</div>
                        {s.message && (
                          <p data-testid={"service-message-" + s.id} className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">{s.message}</p>
                        )}
                      </div>
                    </div>
                    <StatusBadge status={s.status} dataTestId={"service-status-badge-" + s.id} />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <footer className="py-10 mt-12 border-t border-zinc-200 dark:border-zinc-800">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-xs font-mono text-zinc-400 dark:text-zinc-500 uppercase tracking-widest">Last updated</p>
              <p data-testid="last-updated-timestamp" className="mt-0.5 text-sm font-mono text-zinc-600 dark:text-zinc-400">
                {formatTimestamp(timestamp)}
              </p>
            </div>
            <RefreshCountdown countdown={countdown} refreshing={refreshing} />
          </div>
          <p className="mt-6 text-xs text-zinc-300 dark:text-zinc-600">
            Status sourced from UISP. Hierarchy auto-detected from site parent relationships. Polling every {REFRESH_INTERVAL}s.
          </p>
        </footer>
      </div>
    </div>
  );
}
